import { watch } from 'vue';

/*
    Code which handles FACET functionality
*/
export function useFacets(
    categoriesStore,
    facetsQuery,
    facetsStore,
    queryParams,
    paramsFromUrl,
    settings,
    useFetch,
) {
    /*
        Take json from graphql query and simplify it into
        an array of objects, named 'facetsArray',
        which is structured as follows:

        [
            {
                id: int
                title: string
                facetOptions [
                    {
                        id: int
                        title: string
                        checked: bool
                    },
                    etc.
                ]
            },
            etc.
        ]

        This makes it easier to manipulate the data in the app
    */
    async function createFacetsArray(json) {

        let facetsArray = [];

        if (json.data) {

            for (let i = 0; i < json.data.facets.length; i++) {

                const facet = json.data.facets[i];

                const facetOptions = facet.facetOptions;

                let options = [];

                for (let j = 0; j < facetOptions.length; j++) {

                    const option = facetOptions[j];

                    options.push(
                        {
                            id: option.id,
                            title: option.title,
                            checked: false,
                        }
                    )
                }

                facetsArray.push(
                    {
                        id: facet.id,
                        title: facet.title,
                        facetOptions: options,
                    }
                )

                if (i === json.data.facets.length - 1) {

                    return facetsArray;

                }

            }

        }

    }

    /*
        Our graphql queries are set up to accept a 'facets' param.
        This function sets the chosen facet filters, if at least one
        facet option id can be found in our 'facets' param
    */
    function setChosenFacetsIfRequired(facets) {
        if (paramsFromUrl['facets']) {

            const chosenFacets = paramsFromUrl['facets'].split(',');

            for (let i = 0; i < chosenFacets.length; i++) {

                const id = parseInt(chosenFacets[i]);

                queryParams.pushToParamArray('facets', id);

                for (let j = 0; j < facets.length; j++) {

                    const options = facets[j].facetOptions;

                    for (let k = 0; k < options.length; k++) {

                        const option = options[k];

                        if (parseInt(option.id) === id) {

                            option.checked = true;

                            if (i === chosenFacets.length - 1) {

                                facetsStore.updateFacets(facets);

                                return;

                            }

                        }

                        if (
                            i === chosenFacets.length - 1 &&
                            j === facets.length - 1 &&
                            k === options.length - 1
                        ) {

                            facetsStore.updateFacets(facets);

                            return;

                        }

                    }

                }

            }

        }
    }

    /*
        Take json from graphql query and turn it into a useable
        array (using createFacetsArray, above).

        Then, check if any of the facets need to have 'checked = true'
        on page load, by checking against routing params
    */
    async function setInitialFacets(json) {

        let facets = await createFacetsArray(json);

        if (!facets) {

            return;

        }

        /*
            if admin has selected facet filters, then remove all other facet options
            from the array!
        */
        if (settings.facets.length) {

            const adminFacets = facets.filter((facet) => {

                facet.facetOptions = facet.facetOptions.filter((option) => {

                    return settings.facets.map(Number).includes(parseInt(option.id));

                })

                return facet.facetOptions.length > 0;

            });
            facetsStore.updateFacets(adminFacets);
            setChosenFacetsIfRequired(adminFacets)

        } else {

            facetsStore.updateFacets(facets);
            setChosenFacetsIfRequired(facets)

        }

    }

    /*
        Initial setup: this undertakes the initial graphql query
        for facets and sends the resulting json to the above functions
    */
    async function getFacets(){

        const categoriesArray = [];

        const topCats = categoriesStore.topLevelCategories;

        for (let i = 0; i < topCats.length; i++) {

            categoriesArray.push(parseInt(topCats[i].id));

        }

        useFetch({
            url: settings['host'],
            apiKey: settings['key'],
            query: facetsQuery({ categoryIds: categoriesArray }),
            callback: setInitialFacets,
        });

    }

    /*
        every time the facets store changes (which will happen if we
        check / uncheck a facet):
            1. reset the 'facets' query param!
            3. update the 'facets' query param with new data
    */
    function watchFacets() {
        watch(facetsStore, () => {

            queryParams.resetParamArray(
                'facets'
            );

            const topLevel = facetsStore.facets;

            for (let i = 0; i < topLevel.length; i++) {

                const options = topLevel[i].facetOptions;

                for (let j = 0; j < options.length; j++) {

                    if (options[j].checked === true) {

                        queryParams.pushToParamArray('facets', parseInt(options[j].id));

                    };

                }

            }

        });
    }

    return {
        getFacets,
        watchFacets,
    }

}
